import { useRef, useState } from "react";
import QRCode from "qrcode.react";
import { Dialog, IconButton, Stack, Button, TextField, useTheme, Tooltip, ClickAwayListener } from "@mui/material";
import { ClipboardCopy, X } from "lucide-react";
import routes from "@/constants/routes";

interface Props {
  alias: string;
  menuId: string;
  title: string;
  open: boolean;
  onClose: () => void;
}

const ShareLinks = ({ alias, open, onClose, title, menuId }: Props) => {
  const { palette } = useTheme();
  const [openTooltip, setOpenTooltip] = useState(false);

  const qrRef = useRef(null);

  const menuLink = `${document.location.origin}${routes.organizations.menu.read.replace(":organizationAlias", alias).replace(":menuId", menuId)}`;

  const downloadQRCode = () => {
    const fileName = "qrcode.png";

    if (qrRef.current) {
      const canvasElement = qrRef.current.querySelector("canvas");
      if (canvasElement) {
        const pngUrl = canvasElement.toDataURL("image/png").replace("image/png", "image/octet-stream");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } else {
        console.error("Canvas element not found for QR code. Ensure renderAs='canvas'.");
      }
    } else {
      console.error("QR Code reference is not available.");
    }
  };

  const copyUrl = () => {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      navigator.clipboard
        .writeText(menuLink)
        .then(() => {
          setOpenTooltip(true);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    } else {
      console.error("Clipboard API not available.");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ sx: { backgroundColor: "#DDEAFD" } }} maxWidth={"md"}>
        <Stack spacing={2} alignItems="center" justifyContent="center">
          <IconButton
            size="large"
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
            }}
          >
            <X color={palette.primary.main} />
          </IconButton>

          <Stack spacing={2} paddingBlock={3}>
            {alias && menuId && (
              <>
                <div ref={qrRef} style={{ display: "none" }}>
                  <QRCode value={`${document.location.origin}${routes.organizations.root.replace(":organizationAlias", alias)}`} size={256} level="H" includeMargin={true} />
                </div>

                <QRCode
                  size={150}
                  level="H"
                  value={menuLink}
                  style={{
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                  }}
                />
              </>
            )}

            <Button color="success" onClick={downloadQRCode}>
              {title}
            </Button>
          </Stack>

          <Stack padding={2} bgcolor={"#FFFFFF"}>
            <TextField
              value={menuLink}
              slotProps={{
                input: {
                  endAdornment: (
                    <ClickAwayListener onClickAway={() => setOpenTooltip(false)}>
                      <Tooltip
                        arrow
                        placement="top-end"
                        open={openTooltip}
                        title="Copiado"
                        onClose={() => setOpenTooltip(false)}
                        slotProps={{
                          popper: {
                            disablePortal: true,
                          },
                          arrow: {
                            sx: {
                              color: palette.success.main,
                            },
                          },
                          tooltip: {
                            sx: {
                              backgroundColor: palette.success.main,
                              color: "#fff",
                            },
                          },
                        }}
                      >
                        <IconButton onClick={copyUrl}>
                          <ClipboardCopy color={palette.primary.main} />
                        </IconButton>
                      </Tooltip>
                    </ClickAwayListener>
                  ),
                },
              }}
            />
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
};

export default ShareLinks;
